import { Loading } from '@/components/loaders';
import { AudienceRequiredEmptyState } from '@/features/audiences/components/AudienceRequiredEmptyState';
import { FC, useState } from 'react';
import { FaArrowLeft, FaInfoCircle, FaTrash } from 'react-icons/fa';
import { RefreshIcon } from '@heroicons/react/solid';
import { Trans, useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { Button } from '@/lib';
import { ToastType } from '@/models/toast';
import { useAppDispatch } from '@/reducers';
import { useCreateFlowInstanceMutation } from '@/services/endpoints/workspace-connectors';
import { buildInfoFlowParams } from './domainInfoFlowMapper';
import { EmailSendingDomainRecordCard } from './EmailSendingDomainRecordCard';
import { countPassingRecords, formatDomainDateTime, formatLastChecked, getDomainStatusBadgeClass } from './domainUtils';
import { useEmailSendingDomainDelete } from './useEmailSendingDomainDelete';
import { useEmailSendingDomainDetail } from './useEmailSendingDomainDetail';

type EmailSendingDomainDetailViewProps = {
  domainId: string;
  domainNameHint?: string;
  deleteFlowId?: string;
  connectorInstanceId?: string;
  onBack: () => void;
  onDeleted: () => void;
};

export const EmailSendingDomainDetailView: FC<EmailSendingDomainDetailViewProps> = ({
  domainId,
  domainNameHint,
  deleteFlowId,
  connectorInstanceId,
  onBack,
  onDeleted,
}) => {
  const { t } = useTranslation('campaign');
  const workspace = useWorkspace();
  const { audienceId } = useFirstAudienceId();
  const appDispatch = useAppDispatch();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [createFlowInstance, { isLoading: isVerifying }] = useCreateFlowInstanceMutation();
  const { deleteDomain, isDeleting } = useEmailSendingDomainDelete({ deleteFlowId, connectorInstanceId });
  const { domain, flowParams, missingAudience, isLoading, isError, refetch, infoFlowId } = useEmailSendingDomainDetail({
    domainId,
    domainNameHint,
  });

  const displayDomainName = domain?.name ?? String(flowParams['domain_name'] ?? domainNameHint ?? '');

  const passingCount = domain ? countPassingRecords(domain.records) : 0;

  const handleVerify = async () => {
    if (!domain) {
      return;
    }

    if (!infoFlowId) {
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: String(t('emailWorkspace.domains.missingInfoFlow')),
        })
      );
      return;
    }

    if (!connectorInstanceId) {
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: String(t('emailWorkspace.domains.missingConnectorInstance')),
        })
      );
      return;
    }

    if (!audienceId) {
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: String(t('missingAudience.toast')),
        })
      );
      return;
    }

    try {
      const payload = buildFlowInstancePayload({
        workspaceId: workspace.id,
        connectorFlowId: infoFlowId,
        connectorInstanceId,
        audienceId,
        flowParams: buildInfoFlowParams(domain.id, flowParams),
        flowMappings: {},
      });

      await createFlowInstance(payload).unwrap();
      await refetch();

      appDispatch(
        showToast({
          type: ToastType.SUCCESS,
          title: String(t('emailWorkspace.domains.verifySuccess')),
        })
      );
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message ?? String(t('emailWorkspace.domains.verifyFailed'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: errorMessage,
        })
      );
    }
  };

  const handleDelete = async () => {
    const deleted = await deleteDomain(domainId);

    if (deleted) {
      onDeleted();
    }
  };

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <button type='button' onClick={onBack} className='text-secondary-600 inline-flex items-center gap-2 text-sm font-semibold hover:text-slate-900'>
          <FaArrowLeft className='size-3.5' />
          {t('emailWorkspace.domains.backToList')}
        </button>
        <Loading />
      </div>
    );
  }

  if (missingAudience) {
    return (
      <div className='space-y-6'>
        <button type='button' onClick={onBack} className='text-secondary-600 inline-flex items-center gap-2 text-sm font-semibold hover:text-slate-900'>
          <FaArrowLeft className='size-3.5' />
          {t('emailWorkspace.domains.backToList')}
        </button>
        <AudienceRequiredEmptyState />
      </div>
    );
  }

  if (isError || !domain) {
    return (
      <div className='space-y-6'>
        <button type='button' onClick={onBack} className='text-secondary-600 inline-flex items-center gap-2 text-sm font-semibold hover:text-slate-900'>
          <FaArrowLeft className='size-3.5' />
          {t('emailWorkspace.domains.backToList')}
        </button>
        <div className='rounded-2xl border border-secondary-border bg-white p-6 text-sm text-secondary-600'>
          {t('emailWorkspace.domains.detailLoadFailed')}
          <Button intent='link-color' className='ml-2' onClick={() => void refetch()} text={t('emailWorkspace.templates.tryAgain')} />
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <button type='button' onClick={onBack} className='text-secondary-600 inline-flex items-center gap-2 text-sm font-semibold hover:text-slate-900'>
        <FaArrowLeft className='size-3.5' />
        {t('emailWorkspace.domains.backToList')}
      </button>

      <div className='space-y-6 rounded-2xl border border-secondary-border bg-white p-6 md:p-8'>
        <div className='flex flex-wrap items-start justify-between gap-4'>
          <div className='space-y-3'>
            <div className='flex flex-wrap items-center gap-3'>
              <h2 className='text-2xl font-bold text-slate-900'>{displayDomainName}</h2>
              <span className={twMerge('rounded-full px-2.5 py-1 text-xs font-semibold', getDomainStatusBadgeClass(domain.status))}>
                {t(`emailWorkspace.domains.status.${domain.status}`)}
              </span>
            </div>
            <div className='text-secondary-600 flex flex-wrap gap-4 text-sm'>
              <span>
                {t('emailWorkspace.domains.recordsSummary', {
                  passing: passingCount,
                  total: domain.records.length,
                })}
              </span>
              <span>
                {t('emailWorkspace.domains.lastChecked')}:{' '}
                <span className='font-semibold text-slate-900'>{formatLastChecked(domain.lastCheckedAt, t('emailWorkspace.domains.never'))}</span>
              </span>
              <span>
                {t('emailWorkspace.domains.added')}: <span className='font-semibold text-slate-900'>{formatDomainDateTime(domain.createdAt)}</span>
              </span>
            </div>
          </div>

          <div className='flex flex-wrap gap-2'>
            <Button
              intent='secondary'
              className='inline-flex items-center gap-2 border-red-200 text-red-600 hover:bg-red-50'
              onClick={() => setShowDeleteConfirm(true)}
            >
              <FaTrash className='size-3.5' />
              {t('emailWorkspace.domains.delete')}
            </Button>
            <Button intent='primary' isLoading={isVerifying} onClick={handleVerify} className='inline-flex items-center gap-2'>
              {!isVerifying ? <RefreshIcon className='size-4' aria-hidden='true' /> : null}
              {t('emailWorkspace.domains.verifyDns')}
            </Button>
          </div>
        </div>

        <div className='flex gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm leading-relaxed text-blue-900'>
          <FaInfoCircle className='mt-0.5 size-4 shrink-0' />
          <p>
            <Trans
              i18nKey='emailWorkspace.domains.dnsInstructions'
              t={t}
              components={[<strong key='verify' />, <strong key='ok' />]}
            />
          </p>
        </div>

        <div className='space-y-4'>
          {domain.records.map((record, index) => (
            <EmailSendingDomainRecordCard key={`${record.host}-${record.value}-${index}`} record={record} index={index} />
          ))}
        </div>

        {showDeleteConfirm ? (
          <div className='rounded-xl border border-red-200 bg-red-50 p-4'>
            <h3 className='text-base font-semibold text-red-800'>{t('emailWorkspace.domains.deleteTitle')}</h3>
            <p className='mt-1 text-sm text-red-700'>{t('emailWorkspace.domains.deleteDescription', { domain: domain.name })}</p>
            <div className='mt-4 flex gap-3'>
              <Button intent='secondary' onClick={() => setShowDeleteConfirm(false)} text={t('emailWorkspace.domains.cancel')} />
              <Button intent='primary' isLoading={isDeleting} onClick={handleDelete} text={t('emailWorkspace.domains.deleteConfirm')} />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
